'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import styles from './HeroV2.module.css';

export default function HeroV2({ locale = 'en' }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const t = useTranslations();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const SLIDES = [
    {
      desktopImage: '/banners/welcome1.png',
      mobileImage: '/banners/mobile_bANNERS/WELCOME.png',
      colorTone: 'rgba(219, 112, 147, 0.15)',
      headlineKey: 'banners.welcome.headline',
      subheadlineKey: 'banners.welcome.subheadline',
      ctaKey: 'banners.welcome.cta',
    },
    {
      desktopImage: '/banners/spring1.png',
      mobileImage: '/banners/mobile_bANNERS/SPRING.png',
      colorTone: 'rgba(144, 238, 144, 0.15)',
      headlineKey: 'banners.spring.headline',
      subheadlineKey: 'banners.spring.subheadline',
      ctaKey: 'banners.spring.cta',
    },
    {
      desktopImage: '/banners/save10_1.png',
      mobileImage: '/banners/mobile_bANNERS/DAILY.png',
      colorTone: 'rgba(255, 218, 185, 0.15)',
      headlineKey: 'banners.save.headline',
      subheadlineKey: 'banners.save.subheadline',
      ctaKey: 'banners.save.cta',
    },
    {
      desktopImage: '/banners/loyalty1.png',
      mobileImage: '/banners/mobile_bANNERS/LOYALTY.png',
      colorTone: 'rgba(176, 196, 222, 0.15)',
      headlineKey: 'banners.loyalty.headline',
      subheadlineKey: 'banners.loyalty.subheadline',
      ctaKey: 'banners.loyalty.cta',
    },
  ];

  // Auto-rotate slides every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrev = () => {
    setCurrentSlide((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
  };

  const currentSlideData = SLIDES[currentSlide];

  return (
    <section className={styles.section}>
      <div className={styles.carousel}>
        {/* Slide Container */}
        <div className={styles.slideWrapper}>
          {/* Image Section */}
          <div className={styles.imageSection}>
            {SLIDES.map((slide, index) => (
              <div
                key={index}
                className={`${styles.slide} ${index === currentSlide ? styles.active : ''}`}
              >
                <Image
                  src={isMobile ? slide.mobileImage : slide.desktopImage}
                  alt="Banner"
                  fill
                  priority={index === 0}
                  sizes="(max-width: 768px) 100vw, 60vw"
                  style={{
                    objectFit: 'cover',
                    objectPosition: isMobile ? 'center bottom' : 'center',
                  }}
                />
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div
            className={styles.ctaSection}
            style={{ backgroundColor: currentSlideData.colorTone }}
          >
            <div className={styles.ctaContent}>
              <h2 className={styles.ctaHeadline}>
                {t(currentSlideData.headlineKey)}
              </h2>
              <p className={styles.ctaSubheadline}>
                {t(currentSlideData.subheadlineKey)}
              </p>
              <Link
                href={`/${locale}/productos`}
                className={styles.ctaButton}
              >
                {t(currentSlideData.ctaKey)}
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button className={styles.prevButton} onClick={goToPrev} aria-label="Previous slide">
          ‹
        </button>
        <button className={styles.nextButton} onClick={goToNext} aria-label="Next slide">
          ›
        </button>

        {/* Dot Indicators */}
        <div className={styles.dotsContainer}>
          {SLIDES.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${index === currentSlide ? styles.activeDot : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
